import axios, { type AxiosError } from "axios";
import * as Sentry from "@sentry/react";

let api = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL || "http://localhost:3000/api",
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("duas-vidas-token");

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Normaliza o caminho para agrupar erros no Sentry: /recursos/abc-123 e
// /recursos/def-456 sao a MESMA falha — sem isto viram grupos distintos
// (e estouram o limite de cardinalidade das tags).
function endpointPattern(endpoint: string): string {
  return endpoint
    .split("?")[0]
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      "/:id",
    )
    .replace(/\/\d+/g, "/:id");
}

function reportApiFailure(
  method: string,
  endpoint: string,
  status: number,
  message: string,
): void {
  // 4xx e uso esperado (validacao, permissao, sessao expirada) — nao reportar.
  if (status < 500) return;

  const pattern = endpointPattern(endpoint);
  Sentry.withScope((scope) => {
    scope.setTag("api_endpoint", pattern);
    scope.setTag("api_status", String(status));
    scope.setContext("request", { method, endpoint, status, message });
    Sentry.captureException(new Error(`API ${status} ${method} ${pattern}`));
  });
}

function reportNetworkFailure(
  method: string,
  endpoint: string,
  cause: unknown,
): void {
  const pattern = endpointPattern(endpoint);
  Sentry.withScope((scope) => {
    scope.setTag("api_endpoint", pattern);
    scope.setTag("api_status", "network_error");
    scope.setContext("request", { method, endpoint });
    Sentry.captureException(
      cause instanceof Error ? cause : new Error(`Falha de rede em ${method} ${pattern}`),
    );
  });
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const method = (error.config?.method || "GET").toUpperCase();
    const endpoint = error.config?.url || "unknown";

    if (error.response) {
      // O servidor respondeu — so 5xx e falha real digna de evento.
      reportApiFailure(
        method,
        endpoint,
        error.response.status,
        error.message,
      );
    } else {
      // Sem resposta: timeout, DNS, CORS, servidor fora do ar etc.
      reportNetworkFailure(method, endpoint, error);
    }

    return Promise.reject(error);
  },
);

export default api;
